import { useState } from 'react';
import { customToast } from '@/components/toast/custom-react-toastify/v1';
import { useUserPageContextValueHook } from '@/contexts/UserPageContextProvider';
import { useLinkCategoryContextValueHook } from '@/views/mypage/contexts/LinkCategoryContextProvider';
import { useQueryParamsHook } from '@/hooks/useQueryParamsHook';

/**
 * 유튜브 주소 → 영상 ID (미리보기용). 서버 LinkItemsMainService.parseYoutubeUrl 과 같은 규칙.
 */
export function parseYoutubeUrl(url) {
    let parsed;
    try {
        parsed = new URL(String(url).trim());
    } catch (error) {
        return null;
    }
    if (!/^https?:$/.test(parsed.protocol)) {
        return null;
    }

    const host = parsed.hostname.replace(/^(www\.|m\.|music\.)/, '');
    const [, first, second] = parsed.pathname.split('/');
    let videoId = null;

    if (host === 'youtu.be') {
        videoId = first;
    } else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
        videoId = first === 'watch' ? parsed.searchParams.get('v') : ['shorts', 'embed', 'live'].includes(first) ? second : null;
    }

    return /^[A-Za-z0-9_-]{11}$/.test(videoId || '') ? { videoId, isShorts: first === 'shorts' } : null;
}

export const youtubeThumbnail = (videoId) => `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

/**
 * 영상 모듈 입력 폼 (추가·수정 공용, 1차: 유튜브).
 * onSubmit 에는 서버 link-items/create, update/info 에 보낼 body(id 제외)를 넘긴다.
 */
export function WegetVideoModuleForm({ initial, submitLabel, onSubmit }) {
    const userPageContextValueHook = useUserPageContextValueHook();
    const linkCategoryContextValueHook = useLinkCategoryContextValueHook();
    const lcId = useQueryParamsHook()?.params?.lcId;
    const categories = linkCategoryContextValueHook?.content || [];

    const [videoUrl, setVideoUrl] = useState(initial?.linkUrl || '');
    const [title, setTitle] = useState(initial?.title || '');
    const [linkCategoryId, setLinkCategoryId] = useState(
        categories.find(r => r.id === (initial ? initial.linkCategoryId : lcId))?.id || ''
    );

    const youtube = parseYoutubeUrl(videoUrl);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!youtube) {
            customToast.error('유튜브 영상 주소를 입력해 주세요.\nex) https://youtu.be/xxxxxxxxxxx');
            return;
        }

        onSubmit({
            type: 'VIDEO',
            title: title.trim(),
            videoUrl: videoUrl.trim(),
            linkCategoryId: linkCategoryId || '',
            userPageId: userPageContextValueHook?.userPage?.id,
        });
    }

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-5 px-1 pb-2">
            <label className="flex flex-col gap-1">
                <span className="text-sm font-bold"><span className="text-[var(--redColor1)]">*</span> 유튜브 영상 주소 <span className="font-normal text-gray-400">일반 영상 · 쇼츠</span></span>
                <input
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://youtu.be/..."
                    inputMode="url"
                    autoFocus={!initial}
                    className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-gray-900"
                />
                {videoUrl.trim() && !youtube &&
                    <span className="text-[12px] text-[var(--redColor1)]">유튜브 영상 주소가 아니에요. 영상 페이지의 &lsquo;공유 → 링크 복사&rsquo; 주소를 붙여 넣어 주세요.</span>
                }
            </label>

            {youtube &&
                <div className={`relative mx-auto w-full overflow-hidden rounded-xl bg-gray-900 ${youtube.isShorts ? 'aspect-[9/16] max-w-[220px]' : 'aspect-video'}`}>
                    <img src={youtubeThumbnail(youtube.videoId)} alt="영상 미리보기" className="absolute inset-0 h-full w-full object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center">
                        <span className="flex h-12 w-16 items-center justify-center rounded-2xl bg-black/70 text-xl text-white">▶</span>
                    </span>
                    <span className="absolute left-2 top-2 rounded bg-black/60 px-1.5 py-0.5 text-[11px] font-bold text-white">{youtube.isShorts ? '쇼츠 · 세로' : '영상 · 16:9'}</span>
                </div>
            }

            <label className="flex flex-col gap-1">
                <span className="text-sm font-bold">제목 <span className="font-normal text-gray-400">선택 · 영상 아래에 표시</span></span>
                <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={50} placeholder="예: 신상 언박싱" className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-gray-900" />
            </label>

            {categories.length > 0 &&
                <label className="flex flex-col gap-1">
                    <span className="text-sm font-bold">카테고리</span>
                    <select value={linkCategoryId} onChange={(e) => setLinkCategoryId(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-gray-900">
                        <option value="">전체</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </label>
            }

            <button type="submit" className="rounded-full bg-gray-900 py-3.5 text-[15px] font-bold text-white hover:bg-gray-700">
                {submitLabel}
            </button>
        </form>
    );
}
